import type { NativePaymentMethod, NativeSale } from '../types/nativeSales';
import { hasConfirmedPayment } from './zettlePos';

/**
 * Native POS Lifecycle (frontend): textos y decisiones puras del ciclo de cobro.
 *
 * REGLA DE LENGUAJE: la persona que atiende nunca lee un identificador técnico. Nada de
 * "PAYMENT_PENDING" ni "state transition invalid": frases que se entienden de pie, con el cliente
 * enfrente.
 */

/**
 * Etiquetas honestas de método. Negocio360 NO procesa cobros: tarjeta y transferencia se registran
 * como cobros hechos por fuera, y el texto lo dice para que nadie crea que aquí se cobró la tarjeta.
 */
export const NATIVE_PAYMENT_METHODS: Array<{ value: NativePaymentMethod; label: string; hint: string }> = [
  { value: 'CASH', label: 'Efectivo', hint: 'Dinero que recibiste en la caja' },
  { value: 'CARD', label: 'Tarjeta', hint: 'Cobrada en tu terminal, fuera de Negocio360' },
  { value: 'TRANSFER', label: 'Transferencia', hint: 'Te depositaron a tu cuenta' },
  { value: 'OTHER', label: 'Otro', hint: 'Cualquier otra forma de cobro' },
];

/** Solo el efectivo entra al efectivo esperado del corte; el resto no pasó por la caja. */
export const AFFECTS_CASH_DRAWER: NativePaymentMethod[] = ['CASH'];

export function paymentMethodLabel(method: string): string {
  return NATIVE_PAYMENT_METHODS.find((option) => option.value === method)?.label ?? 'Otro';
}

/**
 * Qué debe ver la persona según el estado de la venta. Se decide aquí (puro, probable) y no dentro
 * del componente, para que el mensaje sea consistente venga de donde venga.
 */
export function describeSaleOutcome(sale: NativeSale): { tone: 'success' | 'info' | 'warning'; title: string; detail: string } {
  switch (sale.status) {
    case 'CONFIRMED':
      return {
        tone: 'success',
        title: sale.folio ? `Cobro registrado · ${sale.folio}` : 'Cobro registrado',
        detail: 'La venta quedó completada. No hay que cobrarla de nuevo.',
      };
    case 'CANCELED':
      return { tone: 'info', title: 'Venta cancelada', detail: 'No se registró ningún cobro y no se descontó inventario.' };
    case 'VOIDED':
      return { tone: 'warning', title: 'Venta anulada', detail: 'Esta venta se deshizo después de haberse cobrado.' };
    default:
      // FI-53: una venta que sigue por completar con el cobro YA confirmado no está «pendiente de
      // cobro», y decir que «falta registrar el cobro» invita a cobrarla dos veces.
      if (hasConfirmedPayment(sale.payments)) {
        return {
          tone: 'warning',
          title: 'Cobro confirmado · falta completar la venta',
          detail: 'El cobro con tarjeta ya quedó confirmado. No la cobres de nuevo: ábrela con «Continuar», en las ventas guardadas, para completarla.',
        };
      }
      return { tone: 'info', title: 'Pendiente de cobro', detail: 'La venta está guardada. Falta registrar el cobro.' };
  }
}

/**
 * Deuda (k): cancelar tiene que VERSE. Hasta P-021 la cancelación funcionaba en el servidor y no
 * dejaba ningún rastro en pantalla.
 *
 * Dice "No se registró ningún cobro" y NO "no se cobró nada": lo segundo es una afirmación sobre el
 * mundo que la app no puede sostener (un intento cancelado puede esconder un cargo capturado, deuda
 * (o)). Lo primero es verdad siempre y es lo que la persona necesita saber.
 */
export function describeCanceledSale(options: { cart: 'cleared' | 'kept' | 'empty' }): { title: string; detail: string } {
  const base = 'No se registró ningún cobro y no se descontó inventario.';
  const about = {
    cleared: ' El pedido quedó vacío: ya puedes empezar la venta del siguiente cliente.',
    // No se afirma que el pedido «sigue igual» si no había ninguno.
    kept: ' El pedido que tenías sigue igual.',
    empty: '',
  }[options.cart];
  return { title: 'Venta cancelada', detail: base + about };
}

/**
 * Texto del aviso de recuperación: honesto sobre cuántas ventas quedaron a medias.
 *
 * FI-53: si alguna ya tiene el cobro confirmado (`anyPaid`), «sin cobrar» sería falso para ella: se
 * dice «sin terminar», que es cierto de todas.
 */
export function describePendingRecovery(count: number, anyPaid = false): string {
  const how = anyPaid ? 'sin terminar' : 'sin cobrar';
  if (count === 1) return `Tienes 1 venta guardada ${how}.`;
  return `Tienes ${count} ventas guardadas ${how}.`;
}

/**
 * Hora corta para reconocer una venta pendiente ("la de hace un rato"). Sin segundos: la precisión
 * no ayuda a identificarla y ensucia la tarjeta.
 *
 * `timeZone` es la ZONA DE LA TIENDA y hay que pasarla siempre que se conozca. Sin ella se usa el
 * reloj del equipo, y el 2026-09-30 quedó medido en campo que el equipo del mostrador corría una
 * hora adelantado respecto a su tienda: la misma venta se leía «5:10 p. m.» en la barra de estado y
 * «4:09 p. m.» en su detalle, que sí formatea en zona de tienda. El resultado era que esta etiqueta
 * y el Historial —dos sitios de la misma app— daban horas distintas para la misma venta, y esa es
 * justo la etiqueta que alguien va a cruzar con el panel del proveedor para decidir si hubo un
 * cobro. Quien lee esto decide sobre dinero: las dos horas tienen que ser la misma.
 */
export function shortTime(iso: string | null, timeZone?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // Una zona INVÁLIDA hace que toLocaleTimeString lance un RangeError, y esta etiqueta vive dentro
  // de la tarjeta que avisa de dinero posiblemente cobrado: no puede romper el dibujado. El valor viene
  // del registro de la tienda, que puede traer una zona capturada antes de que existiera el selector,
  // así que es alcanzable desde los datos. Si falla, se degrada al reloj del equipo —peor hora, pero
  // la tarjeta se ve— en vez de desaparecer.
  try {
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone });
  } catch {
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  }
}

/**
 * PAYPAL-DECLINE-01 · lote 5 · el texto de la pantalla con la que el dueño cierra una venta cuyo
 * cobro con tarjeta el servidor no pudo verificar. Aprobado por el dueño el 2026-10-01.
 *
 * Tres decisiones del texto, y las tres son deliberadas:
 *
 * 1. **No dice «verificamos que no hubo cobro».** Dice que lo declara ÉL. El servidor no puede
 *    verificarlo —pregunta al proveedor con la credencial de su organización y la transacción nació
 *    en otra—, así que afirmarlo sería mentir en la pantalla que decide sobre dinero.
 * 2. **No ofrece cobrar en efectivo.** Ese botón, preseleccionado, fue la causa de la venta fantasma
 *    del folio 7: una empleada confirmó en efectivo un cobro que nadie recibió.
 * 3. **La hora viene del servidor y en zona de la TIENDA.** Es la hora que el dueño va a cruzar con
 *    el panel del proveedor, y el reloj del equipo del mostrador estuvo una hora adelantado (§25.17).
 *
 * El FUNDAMENTO no va aquí: es texto libre que escribe el dueño y se guarda con su declaración. Si
 * estuviera fijo en el código, una venta futura afirmaría que se comprobó algo que nadie comprobó.
 */
export interface DeclareUnpaidCopy {
  title: string;
  saleLine: string;
  why: string;
  basisLabel: string;
  basisHint: string;
  commitment: string;
  cancel: string;
  confirm: string;
}

export function describeDeclareUnpaid(
  sale: { totalAmount: string | number; createdAt: string | null },
  timeZone?: string,
): DeclareUnpaidCopy {
  const money = `$${Number(sale.totalAmount).toFixed(2)}`;
  const when = longDateTime(sale.createdAt, timeZone);
  return {
    title: 'Cerrar una venta con un cobro sin resolver',
    saleLine: when ? `Venta de ${money} del ${when} (hora de la tienda).` : `Venta de ${money}.`,
    // NO afirma POR QUÉ. La primera versión decía «porque la transacción se originó en otra cuenta
    // de Zettle», que era la causa del incidente del 2026-09-29 cableada en código general: en
    // cualquier otra venta esa frase es falsa, y además le daba al dueño una explicación inventada
    // justo antes de pedirle que declarara. La causa concreta, si se conoce, es parte de SU
    // fundamento, no de la pantalla.
    why: 'Esta venta intentó cobrarse con el lector y el cobro quedó sin resolver. El servidor no ha podido verificarlo con el proveedor.',
    basisLabel: 'En qué te basas',
    basisHint: 'Queda guardado con tu declaración. Escribe lo que compruebes, con tus palabras.',
    commitment:
      'Al confirmar, tú declaras que esta venta no se cobró, y se cierra como cancelada. Queda '
      + 'registrado quién lo declaró y cuándo.',
    cancel: 'Cancelar',
    confirm: 'Declaro que no se cobró y cierro la venta',
  };
}

/**
 * Ejemplo de la CLASE de cosas que se esperan en el fundamento, para mostrar como `placeholder` y
 * nunca como contenido inicial.
 *
 * Nació prerrellenado y una revisión adversarial lo marcó: con el texto ya escrito, el botón de
 * confirmar nacía habilitado y se podía declarar sin leer, dejando como registro tres afirmaciones
 * de hecho que quizá nadie comprobó. Una declaración que se firma sola no es una declaración.
 */
export const DECLARE_UNPAID_BASIS_SUGGESTION = [
  'El lector mostró «Pago rechazado» en pantalla.',
  'Se consultó esa otra cuenta con la app oficial de PayPal/Zettle y no se encontró ningún cobro.',
  'Esa cuenta está registrada en otro país y en otra moneda.',
].map((line) => `· ${line}`).join('\n');

/** Mismo criterio que `shortTime`: la zona de la TIENDA manda, y una zona inválida no rompe nada. */
function longDateTime(iso: string | null, timeZone?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat('es-MX', { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('es-MX', options).format(date);
  }
}
